import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Comment } from '@/types/social';
import CommentReply from './CommentReply';
import CommentReplyInput from './CommentReplyInput';
import { getCommentReplies, createCommentReply } from '@/lib/supabase/social';

interface CommentRepliesProps {
  commentId: string;
  initialRepliesCount: number;
}

const CommentReplies: React.FC<CommentRepliesProps> = ({
  commentId,
  initialRepliesCount,
}) => {
  const [replies, setReplies] = useState<Comment[]>([]);
  const [repliesCount, setRepliesCount] = useState(initialRepliesCount);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);

  const fetchReplies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (!commentId) throw new Error('Invalid comment ID');
      const fetched = await getCommentReplies(commentId);
      setReplies(fetched);
      setRepliesCount(fetched.length);
    } catch (err) {
      console.error('Error fetching replies:', err);
      setError('Failed to load replies');
    } finally {
      setIsLoading(false);
    }
  };

  // On mount — expand replies if targeted via hash
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const isTarget = hash.startsWith('#reply-');
    const targetReplyId = hash.replace('#reply-', '');

    if (isTarget) {
      setShowReplies(true);

      const scrollToReply = () => {
        const el = document.getElementById(`reply-${targetReplyId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-gold-400', 'rounded-lg');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-gold-400', 'rounded-lg');
          }, 2000);
        }
      };

      if (replies.length > 0) {
        scrollToReply();
      } else {
        fetchReplies().then(scrollToReply).catch(console.error);
      }
    } else {
      setShowReplies(initialRepliesCount > 0);
    }

    setRepliesCount(initialRepliesCount);
  }, []);

  useEffect(() => {
    if (initialRepliesCount > repliesCount) {
      setRepliesCount(initialRepliesCount);
      if (showReplies) {
        fetchReplies();
      }
    }
  }, [initialRepliesCount]);

  const handleSubmitReply = async (parentId: string, content: string) => {
    try {
      if (!parentId || !content.trim()) throw new Error('Missing data');
      const newReply = await createCommentReply(parentId, content.trim());
      if (showReplies) {
        setReplies(prev => [newReply, ...prev]);
      }
      setRepliesCount(prev => prev + 1);
      setShowReplyInput(false);
      setShowReplies(true);
      if (!showReplies || replies.length === 0) {
        fetchReplies();
      }
    } catch (err) {
      console.error('Error creating reply:', err);
      throw err;
    }
  };

  const toggleReplies = () => {
    const newShowReplies = !showReplies;
    setShowReplies(newShowReplies);
    if (newShowReplies && replies.length === 0 && repliesCount > 0) {
      fetchReplies();
    }
  };

  if (repliesCount === 0 && !showReplyInput && !showReplies) return null;

  return (
    <div className="mt-2 ml-8">
      {repliesCount > 0 && (
        <button
          onClick={toggleReplies}
          className="flex items-center text-xs text-gold-400 hover:text-gold-500 transition-colors mb-2"
        >
          {showReplies ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Hide {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}
            </>
          )}
        </button>
      )}

      <AnimatePresence>
        {showReplyInput && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CommentReplyInput
              commentId={commentId}
              onSubmit={handleSubmitReply}
              onCancel={() => setShowReplyInput(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showReplies && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            {isLoading ? (
              <div className="flex justify-center py-2">
                <div className="w-5 h-5 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="text-red-400 text-xs">{error}</div>
            ) : replies.length > 0 ? (
              replies.map(reply => {
                const isTarget =
                  typeof window !== 'undefined' &&
                  window.location.hash === `#reply-${reply.id}`;
                return (
                  <div
                    key={reply.id}
                    id={`reply-${reply.id}`}
                    className={isTarget ? 'bg-yellow-100 dark:bg-dark-200 rounded-xl p-1 transition-all' : ''}
                  >
                    <CommentReply
                      reply={reply}
                      onReply={() => setShowReplyInput(true)}
                    />
                  </div>
                );
              })
            ) : (
              <div className="text-dark-800 text-xs">No replies yet</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommentReplies;
